/**
 * 轻量级事件总线模块
 * 用于在不同组件/模块之间进行事件通信
 *
 * @example
 * import eventBus from './event-bus'
 *
 * // 监听事件
 * eventBus.on('user-login', (userData) => {
 *   console.log('用户登录:', userData)
 * })
 *
 * // 触发事件
 * eventBus.emit('user-login', { id: 1, name: '张三' })
 *
 * // 移除事件监听
 * eventBus.off('user-login', handlerFunction)
 */

class EventBus {
	constructor() {
		// 存储事件监听器的对象
		this.events = {};
	}

	/**
	 * 监听事件
	 * @param {string} event - 事件名称
	 * @param {Function} callback - 回调函数
	 * @returns {Function} - 返回取消监听的函数
	 */
	on(event, callback) {
		if (typeof event !== 'string') {
			console.warn('EventBus.on: 事件名称必须是字符串');
			return () => {};
		}

		if (typeof callback !== 'function') {
			console.warn('EventBus.on: 回调函数必须是函数类型');
			return () => {};
		}

		// 如果该事件还没有监听器列表，则创建一个
		if (!this.events[event]) {
			this.events[event] = [];
		}

		// 添加回调函数到监听器列表
		this.events[event].push(callback);

		// 返回取消监听的函数
		return () => this.off(event, callback);
	}

	/**
	 * 移除事件监听
	 * @param {string} event - 事件名称
	 * @param {Function} callback - 要移除的回调函数，如果不传则移除该事件的所有监听器
	 */
	off(event, callback) {
		if (typeof event !== 'string') {
			console.warn('EventBus.off: 事件名称必须是字符串');
			return;
		}

		// 如果该事件没有监听器，直接返回
		if (!this.events[event]) {
			return;
		}

		// 如果没有传入回调函数，则移除该事件的所有监听器
		if (!callback) {
			delete this.events[event];
			return;
		}

		// 从监听器列表中移除指定的回调函数
		const index = this.events[event].indexOf(callback);
		if (index > -1) {
			this.events[event].splice(index, 1);
		}

		// 如果该事件没有监听器了，删除该事件
		if (this.events[event].length === 0) {
			delete this.events[event];
		}
	}

	/**
	 * 触发事件
	 * @param {string} event - 事件名称
	 * @param {...any} args - 传递给回调函数的参数
	 */
	emit(event, ...args) {
		if (typeof event !== 'string') {
			console.warn('EventBus.emit: 事件名称必须是字符串');
			return;
		}

		// 如果该事件没有监听器，直接返回
		if (!this.events[event]) {
			return;
		}

		// 执行所有监听器
		this.events[event].forEach(callback => {
			try {
				callback(...args);
			} catch (error) {
				console.error(`EventBus: 事件 "${event}" 的监听器执行出错:`, error);
			}
		});
	}

	/**
	 * 只监听一次事件（触发后自动移除监听器）
	 * @param {string} event - 事件名称
	 * @param {Function} callback - 回调函数
	 * @returns {Function} - 返回取消监听的函数
	 */
	once(event, callback) {
		if (typeof event !== 'string') {
			console.warn('EventBus.once: 事件名称必须是字符串');
			return () => {};
		}

		if (typeof callback !== 'function') {
			console.warn('EventBus.once: 回调函数必须是函数类型');
			return () => {};
		}

		// 创建一个包装函数，执行后自动移除监听器
		const onceWrapper = (...args) => {
			this.off(event, onceWrapper);
			callback(...args);
		};

		// 将原始回调函数保存到包装函数上，方便手动取消监听
		onceWrapper.originalCallback = callback;

		return this.on(event, onceWrapper);
	}

	/**
	 * 清除所有事件监听器
	 * @param {string} [event] - 可选，指定要清除的事件名称，如果不传则清除所有事件
	 */
	clear(event) {
		if (event) {
			// 清除指定事件的所有监听器
			delete this.events[event];
		} else {
			// 清除所有事件监听器
			this.events = {};
		}
	}

	/**
	 * 获取指定事件的监听器数量
	 * @param {string} event - 事件名称
	 * @returns {number} - 监听器数量
	 */
	listenerCount(event) {
		if (typeof event !== 'string') {
			return 0;
		}
		return this.events[event] ? this.events[event].length : 0;
	}

	/**
	 * 获取所有事件名称
	 * @returns {string[]} - 事件名称数组
	 */
	eventNames() {
		return Object.keys(this.events);
	}

	/**
	 * 检查是否有指定事件的监听器
	 * @param {string} event - 事件名称
	 * @returns {boolean} - 是否有监听器
	 */
	hasListeners(event) {
		return this.listenerCount(event) > 0;
	}
}

// 创建全局事件总线实例
export const eventBus = new EventBus();

// 同时导出类，方便创建独立的事件总线实例
export { EventBus };

// 默认导出全局实例
export default eventBus;
